import { useState, useEffect, useCallback } from 'react';
import analyticsService from '../../services/analyticsService';
import './ProductAnalytics.css';

/**
 * Product Analytics Component
 * Displays product performance metrics and insights
 * Data sources: Google Analytics 4 (views) + Orders Database (conversions/revenue)
 */
function ProductAnalytics({ productId = null }) {
  const [timeRange, setTimeRange] = useState('30d'); // '7d', '30d', '90d', '1y'
  const [sortBy] = useState('revenue'); // 'revenue', 'conversions', 'views'
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch product metrics from API
  const fetchMetrics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await analyticsService.getProductMetrics(timeRange, 5, sortBy);

      if (response.data.success) {
        setMetrics(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch metrics');
      }
    } catch (err) {
      console.error('Error fetching product metrics:', err);
      setError('Failed to load product analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange, sortBy]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (isLoading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <span>Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-error">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <h3>Unable to Load Analytics</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchMetrics}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div>
          <h2>Product Analytics</h2>
          <p className="analytics-subtitle">
            {productId ? 'Individual Product Performance' : 'Overall Product Performance'}
          </p>
        </div>

        <div className="time-range-selector">
          <button
            className={`range-btn ${timeRange === '7d' ? 'active' : ''}`}
            onClick={() => setTimeRange('7d')}
          >
            7 Days
          </button>
          <button
            className={`range-btn ${timeRange === '30d' ? 'active' : ''}`}
            onClick={() => setTimeRange('30d')}
          >
            30 Days
          </button>
          <button
            className={`range-btn ${timeRange === '90d' ? 'active' : ''}`}
            onClick={() => setTimeRange('90d')}
          >
            90 Days
          </button>
          <button
            className={`range-btn ${timeRange === '1y' ? 'active' : ''}`}
            onClick={() => setTimeRange('1y')}
          >
            1 Year
          </button>
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard
          title="Total Views"
          value={metrics.summary.totalViews.toLocaleString()}
          change={metrics.summary.viewsChange}
          direction={metrics.summary.viewsChangeDirection}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />

        <MetricCard
          title="Conversions"
          value={metrics.summary.totalConversions.toLocaleString()}
          change={metrics.summary.conversionsChange}
          direction={metrics.summary.conversionsChangeDirection}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
            </svg>
          }
        />

        <MetricCard
          title="Revenue"
          value={`£${metrics.summary.totalRevenue.toLocaleString()}`}
          change={metrics.summary.revenueChange}
          direction={metrics.summary.revenueChangeDirection}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        <MetricCard
          title="Conversion Rate"
          value={`${metrics.summary.conversionRate}%`}
          change={metrics.summary.conversionRateChange}
          direction={metrics.summary.conversionRateChangeDirection}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
          }
        />
      </div>

      {!productId && metrics.topProducts && metrics.topProducts.length > 0 && (
        <div className="top-products-section">
          <h3>Top Performing Products</h3>
          <div className="top-products-list">
            {metrics.topProducts.map((product) => (
              <div key={product.id} className="top-product-item">
                <div className="product-rank">#{product.rank}</div>
                <div className="product-thumbnail">
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt={product.name} />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <div className="product-name">{product.name}</div>
                  <div className="product-sku">{product.sku}</div>
                  <div className="product-stats">
                    {product.views.toLocaleString()} views · {product.conversions} sales · {product.conversionRate}% conv.
                  </div>
                </div>
                <div className="product-revenue">£{product.revenue.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="analytics-footer">
        <div className="data-sources">
          <div className="source-item">
            <span className="source-label">Views:</span>
            <span className="source-value">{metrics.summary.viewsSource}</span>
          </div>
          <div className="source-item">
            <span className="source-label">Conversions:</span>
            <span className="source-value">{metrics.summary.conversionsSource}</span>
          </div>
        </div>
        <div className="metadata-info">
          <span>Period: {metrics.metadata.startDate} to {metrics.metadata.endDate}</span>
          <span className="separator">·</span>
          <span>Updated: {new Date(metrics.metadata.generatedAt).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Metric Card Component
 * @param {string} direction - 'up', 'down', or 'unchanged'
 */
function MetricCard({ title, value, change, direction, icon }) {
  const getChangeClass = () => {
    switch (direction) {
      case 'up': return 'positive';
      case 'down': return 'negative';
      default: return 'neutral';
    }
  };

  const getChangeIcon = () => {
    switch (direction) {
      case 'up':
        return <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />;
      case 'down':
        return <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />;
      default:
        return <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15" />;
    }
  };

  const getChangePrefix = () => {
    switch (direction) {
      case 'up': return '+';
      case 'down': return '-';
      default: return '';
    }
  };

  return (
    <div className="metric-card">
      <div className="metric-header">
        <div className="metric-icon">{icon}</div>
        <div className="metric-title">{title}</div>
      </div>
      <div className="metric-value">{value}</div>
      <div className={`metric-change ${getChangeClass()}`}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          {getChangeIcon()}
        </svg>
        <span>{getChangePrefix()}{Math.abs(change).toFixed(1)}%</span>
        <span className="change-label">vs previous period</span>
      </div>
    </div>
  );
}

export default ProductAnalytics;
